import React, { useEffect } from "react"
import * as Reinspect from "reinspect"

import { AppState } from "../../types"
import Loading from "../Views/Loading"
import BrowserNotSupported from "../Views/BrowserNotSupported"

import FetchingFormQuery from "../GeneriskeRapporter/FetchingForm"
import DataReady from "../GeneriskeRapporter/DataReady"
import Error from "../Views/Error"

import {
  ErrorData,
  uiReducer,
  initialUIModel,
  UIModel,
  UIAction,
} from "../../update/ui"
import {
  apiPath,
  fetchVegobjectTyperTilvalg,
  fetchKontraktsomraader,
  fetchDriftskontraktstyperIO,
} from "../../data/api"

import {
  reportReducer,
  initialReportModel,
  ReportModel,
  ReportAction,
} from "../../update/report"
import useBrowserDetect from "../../hooks/useBrowserDetect"
import {
  GeneriskModel,
  GeneriskAction,
  generiskReducer,
  initialGeneriskModel,
  setOmraaderAction,
} from "./update"
import { GeneriskRapportForm } from "./Form"
import { getOmraader } from "@nvdb-rapport/shared/src/api"
import {ReportType} from "../../models/ReportType";

interface AppProps {
  uiState: UIModel;
  uiDispatch: React.Dispatch<UIAction>;
  generiskState: GeneriskModel;
  generiskDispatch: React.Dispatch<GeneriskAction>;
  reportState: ReportModel;
  reportDispatch: React.Dispatch<ReportAction>;
}

function AppStateContainer({
  uiState,
  uiDispatch,
  generiskState,
  generiskDispatch,
  reportState,
  reportDispatch,
}: AppProps): JSX.Element {
  useBrowserDetect({ uiDispatch, appState: uiState.appState })

  useEffect(() => {
    console.log(
      "Running in " +
        process.env.NODE_ENV +
        " mode with REST API server " +
        apiPath
    )
  }, [])
  useEffect(() => {
    async function fetchAreas() {
      if (uiState.appState === AppState.LOADING) {
        // Fetch kontraktsområder
        (await fetchKontraktsomraader()).caseOf({
          Left: e => {
            generiskDispatch({
              type: "GotContractAreas",
              payload: {
                type: "FAILURE",
                error: {
                  title: "Innhenting av kontraktsområder feilet",
                  message: e,
                },
              },
            })
            uiDispatch({ type: "UpdateAppState", payload: AppState.ERROR })
          },
          Right: kontraktsOmraader => {
            generiskDispatch({
              type: "GotContractAreas",
              payload: { type: "SUCCESS", data: kontraktsOmraader },
            })
            uiDispatch({
              type: "UpdateAppState",
              payload: AppState.APP_READY,
            })
          },
        });
        (await fetchDriftskontraktstyperIO()).caseOf({
          Left: e => {
            const errorData = {
              title: "Innhenting av driftskontraktstyper feilet",
              message: e,
            }
            uiDispatch({
              type: "AddError",
              payload: errorData,
            })

            generiskDispatch({
              type: "GotReportTypes",
              payload: { type: "FAILURE", error: errorData },
            })

            uiDispatch({ type: "UpdateAppState", payload: AppState.ERROR })
          },
          Right: kontraktstyper => {
            generiskDispatch({
              type: "GotReportTypes",
              payload: { type: "SUCCESS", data: kontraktstyper },
            })
          },
        });
      }
    }

    fetchAreas()
  }, [uiDispatch, generiskDispatch, uiState])

  useEffect(() => {
    // TODO: AsyncEither?

    if (uiState.appState === AppState.LOADING) {
      generiskDispatch(setOmraaderAction({ type: "LOADING" }))
      // Fetch initial form values
      ;(async function () {
        const omraadeData = await getOmraader(apiPath)
        generiskDispatch(setOmraaderAction(omraadeData))
      })()
      ;(async () => {
        (await fetchVegobjectTyperTilvalg(ReportType.DETALJERT_MENGDEOVERSIKT)).caseOf({
          Left: e => {
            const errorMessage: ErrorData = {
              title: "Feil ved innhenting av objekter",
              message: e,
            }
            uiDispatch({
              type: "AddError",
              payload: errorMessage,
            })
            generiskDispatch({
              type: "SetVegobjektTilvalg",
              payload: {
                type: "FAILURE",
                error: errorMessage,
              },
            })
          },
          Right: data => {
            generiskDispatch({
              type: "SetVegobjektTilvalg",
              payload: { type: "SUCCESS", data },
            })
            generiskDispatch({
              type: "UpdateCustomRemovals",
              payload: data,
            })
            uiDispatch({ type: "UpdateAppState", payload: AppState.APP_READY })
          },
        })
      })()
    }
  }, [uiState])

  switch (uiState.appState) {
    case AppState.DETECTING_BROWSER:
      return <Loading />

    case AppState.UNSUPPORTED_BROWSER:
      return <BrowserNotSupported />

    case AppState.LOADING:
      return <Loading />

    case AppState.APP_READY:
      return (
        <GeneriskRapportForm
          generiskDispatch={generiskDispatch}
          generiskState={generiskState}
          uiDispatch={uiDispatch}
          reportDispatch={reportDispatch}
          uiState={uiState}
        />
      )

    case AppState.FETCHING_FORM_QUERY:
      return (
        <>
          <GeneriskRapportForm
            generiskDispatch={generiskDispatch}
            generiskState={generiskState}
            uiDispatch={uiDispatch}
            reportDispatch={reportDispatch}
            uiState={uiState}
          />
          <FetchingFormQuery
            generiskState={generiskState}
            uiState={uiState}
            uiDispatch={uiDispatch}
            reportDispatch={reportDispatch}
            reportState={reportState}
          />
        </>
      )

    case AppState.CONTRACT_PENDING:
      return (
        <>
          <GeneriskRapportForm
            generiskDispatch={generiskDispatch}
            generiskState={generiskState}
            uiDispatch={uiDispatch}
            reportDispatch={reportDispatch}
            uiState={uiState}
          />
          <FetchingFormQuery
            generiskState={generiskState}
            uiState={uiState}
            uiDispatch={uiDispatch}
            reportDispatch={reportDispatch}
            reportState={reportState}
          />
        </>
      )

    case AppState.DATA_READY:
      return (
        <>
          <GeneriskRapportForm
            generiskDispatch={generiskDispatch}
            generiskState={generiskState}
            uiDispatch={uiDispatch}
            reportDispatch={reportDispatch}
            uiState={uiState}
          />
          <DataReady
            generiskDispatch={generiskDispatch}
            generiskState={generiskState}
            uiDispatch={uiDispatch}
            reportDispatch={reportDispatch}
            reportState={reportState}
          />
        </>
      )

    case AppState.ERROR:
      return <Error uiState={uiState} />

    default:
      return <Loading />
  }
}

function ProductionAppWrapper() {
  const [uiState, uiDispatch] = React.useReducer(uiReducer, initialUIModel)
  const [generiskState, generiskDispatch] = React.useReducer(
    generiskReducer,
    initialGeneriskModel
  )
  const [reportState, reportDispatch] = React.useReducer(
    reportReducer,
    initialReportModel
  )
  return (
    <AppStateContainer
      generiskDispatch={generiskDispatch}
      generiskState={generiskState}
      uiDispatch={uiDispatch}
      uiState={uiState}
      reportDispatch={reportDispatch}
      reportState={reportState}
    />
  )
}

function DevelopmentAppWrapper() {
  const [uiState, uiDispatch] = Reinspect.useReducer(
    uiReducer,
    initialUIModel,
    state => state,
    "UI"
  )
  const [generiskState, generiskDispatch] = Reinspect.useReducer(
    generiskReducer,
    initialGeneriskModel,
    () => initialGeneriskModel,
    "GENERISK"
  )
  const [reportState, reportDispatch] = Reinspect.useReducer(
    reportReducer,
    initialReportModel,
    state => state,
    "RAPPORT"
  )
  return (
    <AppStateContainer
      generiskDispatch={generiskDispatch}
      generiskState={generiskState}
      uiDispatch={uiDispatch}
      uiState={uiState}
      reportDispatch={reportDispatch}
      reportState={reportState}
    />
  )
}

function GenerelleRapporterApp() {
  if (process.env.NODE_ENV === "production") {
    return <ProductionAppWrapper />
  } else {
    return <DevelopmentAppWrapper />
  }
}

export default GenerelleRapporterApp
